/**
 * What the browser does around an entry that has not been submitted yet (PROJECT_SPEC.md ERR-008, ERR-010).
 *
 * ERR-008 — nothing is written until the person submits: typing into a field creates no record, so a crash or a
 * closed tab leaves none. The unsaved entry is DISCARDED (not kept as a hidden draft: a draft of GMP data that
 * nobody signed is a record nobody can account for). The guard below warns before an intentional close.
 *
 * ERR-010 — back/forward cannot re-execute anything: the server refuses a repeat of a completed step
 * (FORBIDDEN_TRANSITION), and when the browser restores a page from its back/forward cache the data on screen
 * is re-read from the server instead of trusted.
 */
export interface PageEvents {
  addEventListener(type: string, fn: (e: Event) => void): void;
  removeEventListener(type: string, fn: (e: Event) => void): void;
}

/** Asks the browser to confirm closing the tab while `isDirty()` is true. Returns the detach function. */
export function attachUnloadGuard(win: PageEvents, isDirty: () => boolean): () => void {
  const onUnload = (e: Event): void => {
    if (!isDirty()) return;
    e.preventDefault();
    (e as BeforeUnloadEvent).returnValue = ""; // required by some browsers to show the prompt
  };
  win.addEventListener("beforeunload", onUnload);
  return () => win.removeEventListener("beforeunload", onUnload);
}

/** When a page comes back from the back/forward cache, run `refresh` so no stale screen offers a stale action. */
export function refreshOnRestore(win: PageEvents, refresh: () => void): () => void {
  const onShow = (e: Event): void => {
    if ((e as PageTransitionEvent).persisted) refresh();
  };
  win.addEventListener("pageshow", onShow);
  return () => win.removeEventListener("pageshow", onShow);
}
